import { format, parse, startOfWeek, getDay, add  } from "date-fns";
import { enUS, es, it, pt } from "date-fns/locale";

import { useEffect, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer  } from "react-big-calendar";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSubscription, useUserInfo } from "store/auth/auth-store";
import { ProfileEnum } from "utils/@types";
import { WorkItemProfiles } from "helpers/profileEnum";
import { ProfileTab } from "enums/ProfileTabs";
import { useGetReprocessMonitorData } from "services/input-core/monitor/get-reprocess-monitor";
import { useKeycloak } from "@react-keycloak/web";
import { CircularProgress } from "@mui/material";

interface Event {
  title: string;
  allDay: boolean;
  start: Date;
  end: Date;
  color: string;
}

interface ActivityItem {
  _id?: string;
  subscription?: string;
  fieldglassId: string;
  activityItemCode: string;
  activityItemDueDate: string;
  profile: string;
}

const locales = { en: enUS, es, it, pt };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const eventStyleGetter = (event: Event) => {
  const backgroundColor = event.color || "lightgray";
  const style = {
    backgroundColor,
    borderRadius: "5px",
    opacity: 0.8,
    color: "white",
    border: "0px",
    display: "block",
    cursor: "pointer",
  };
  return {
    style,
  };
};

export default function ReprocessMonitor() {
  const [reprocessEvents, setReprocessEvents] = useState([]);
  const { t, i18n } = useTranslation("dashboard");
  const [culture, setCulture] = useState(i18n?.language);
  const { isKuo } = useSubscription(); 
  const { keycloak } = useKeycloak();
  const [supplierCode, setSupplierCode] = useState()


  useEffect(() => {
    keycloak.loadUserInfo().then(() => {
      setSupplierCode(JSON.parse((keycloak?.userInfo as any)?.customAttributes?.supplierCode ?? '["none"]')[0]);
    });
  }, []);

  const {
    data: events,
    isLoading: isLoadingReprocessMonitor,
  } = useGetReprocessMonitorData(supplierCode, 
    {
      enabled: !!supplierCode
    }
  );

  useEffect(() => {
    loadReprocessMonitor();
  }, [events]);

  useEffect(() => {
    const onLanguageChange = (newLanguage: string) => {
      setCulture(newLanguage);
    };

    const languageChangeHandler = () => {
      const newLanguage = i18n.language;
      onLanguageChange(newLanguage);
    };

    i18n.on("languageChanged", languageChangeHandler);

    return () => {
      i18n.off("languageChanged", languageChangeHandler);
    };
  }, [i18n]);

  const redirectToProfile = (activityItem: ActivityItem) => {
    const date = new Date(activityItem.activityItemDueDate);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const profile =
      isKuo && activityItem.profile !== ProfileEnum.WORKER
        ? WorkItemProfiles["SOWADM"]
        : WorkItemProfiles[
            activityItem.profile as keyof typeof WorkItemProfiles
          ];

    window.open(
      `../app/${profile}/profile?id=${activityItem.fieldglassId}&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}`,
      "_blank"
    );
  };

  const loadReprocessMonitor = () => {
    setReprocessEvents(
      events?.map((data) => {
        const formattedDate = add(new Date(data.reprocessDate), { hours: 3 });

        return {
          title: `${data.activityItem.activityItemCode} - ${data.activityItem.fieldglassId}`,
          allDay: true,
          start: formattedDate,
          end: formattedDate,
          color: "#FC930A",
          activityItem: data.activityItem,
        };
      })
    );
  };

  return (
    <Card>
      <CardBody calendar={!!reprocessEvents?.length}>
        {isLoadingReprocessMonitor ? (
          <CircularProgress />
        ) : !reprocessEvents?.length ? (
          <h5>{t("no-documents")}</h5>
        ) : (
          <>
            <BigCalendar
              selectable
              localizer={localizer}
              culture={culture}
              events={reprocessEvents}
              defaultView='month'
              scrollToTime={new Date()}
              defaultDate={new Date()}
              onSelectEvent={(event) => redirectToProfile(event.activityItem)}
              eventPropGetter={eventStyleGetter}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}
